import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';
import { defineMessage } from 'react-intl';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  Level,
  useSnackNotificationContext,
} from '@sorare/core/src/contexts/snackNotification';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';

import type {
  RemoveFromWatchlistMutation,
  RemoveFromWatchlistMutationVariables,
} from './__generated__/useRemoveFromWatchlist.graphql.ts';

const REMOVE_FROM_WATCHLIST_MUTATION = gql`
  mutation RemoveFromWatchlistMutation(
    $watchlistId: ID!
    $printablePlayerIds: [ID!]!
  ) {
    removeFromWatchlist(
      input: {
        watchlistId: $watchlistId
        printablePlayerIds: $printablePlayerIds
      }
    ) {
      watchlist {
        slug
        id
        title
        commonPlayers(first: 7) {
          nodes {
            id
            anyPlayer {
              slug
            }
          }
        }
        totalPlayersCount
      }
    }
  }
` as TypedDocumentNode<
  RemoveFromWatchlistMutation,
  RemoveFromWatchlistMutationVariables
>;

const unresolvablePlayer = defineMessage({
  defaultMessage: 'An error has occured. Please try again later.',
});

export const useRemoveFromWatchlist = () => {
  const { formatMessage } = useIntlContext();
  const { showNotification } = useSnackNotificationContext();
  const [mutate, { loading }] = useMutation(REMOVE_FROM_WATCHLIST_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const removeFromWatchlist = useCallback(
    async (watchlistId: string, printablePlayerIds: (string | undefined)[]) => {
      const resolvedIds = printablePlayerIds.filter(Boolean);

      // The mutation reports success for ids it cannot resolve
      if (resolvedIds.length === 0) {
        showNotification(
          'errors',
          { errors: formatMessage(unresolvablePlayer) },
          { level: Level.WARN }
        );
        return undefined;
      }

      return mutate({
        variables: { watchlistId, printablePlayerIds: resolvedIds },
        refetchQueries: [
          namedOperations.Query.AdvancedWatchlistSearchQuery,
          namedOperations.Query.AdvancedWatchlistFacetSearchQuery,
        ],
      });
    },
    [mutate, showNotification, formatMessage]
  );

  return {
    removeFromWatchlist: {
      mutate: removeFromWatchlist,
      loading,
    },
  };
};
